/** The preferred name for each supplied value, with the plain meaning shown to admins. */
export const CLINIC_DOCUMENT_RECORD_SOURCES = [
    // Forms print the patient's legal names, never the preferred name. The chart keeps a legal
    // first name, an optional legal middle name and a legal last name; given_and_middle supplies the
    // first name followed by the middle name when there is one, and the full name includes it too.
    // patient.legal_name.family and patient.legal_name.given_and_middle supply the same two values
    // and are not listed a second time.
    { kind: "patient", path: "patient.full_name", label: "Patient full legal name" },
    { kind: "patient", path: "patient.name.family", label: "Patient legal last name" },
    {
        kind: "patient",
        path: "patient.name.given_and_middle",
        label: "Patient legal first name(s)",
    },
    { kind: "patient", path: "patient.date_of_birth", label: "Patient date of birth (yyyy-mm-dd)" },
    { kind: "patient", path: "patient.date_of_birth.year", label: "Patient birth year" },
    { kind: "patient", path: "patient.date_of_birth.month", label: "Patient birth month" },
    { kind: "patient", path: "patient.date_of_birth.day", label: "Patient birth day" },
    { kind: "patient", path: "patient.health_card_number", label: "Patient health card number" },
    {
        kind: "patient",
        path: "patient.health_card.version_code",
        label: "Patient health card version code",
    },
    { kind: "patient", path: "patient.sex", label: "Patient sex (M or F)" },
    { kind: "patient", path: "patient.sex.male", label: "Tick when patient sex is male" },
    { kind: "patient", path: "patient.sex.female", label: "Tick when patient sex is female" },
    { kind: "patient", path: "patient.phone", label: "Patient phone" },
    { kind: "patient", path: "patient.email", label: "Patient email" },
    { kind: "patient", path: "patient.mrn", label: "Patient chart number (MRN)" },
    { kind: "patient", path: "patient.address", label: "Patient address (full)" },
    {
        kind: "patient",
        path: "patient.address.street",
        label: "Patient street (number and name)",
    },
    { kind: "patient", path: "patient.address.city", label: "Patient city" },
    { kind: "patient", path: "patient.address.province", label: "Patient province" },
    { kind: "patient", path: "patient.address.postal_code", label: "Patient postal code" },
    { kind: "provider", path: "provider.full_name", label: "Clinician full name" },
    // Saved in the physician profile as the legal names; blank until saved, never a split of the
    // full name.
    { kind: "provider", path: "provider.name.family", label: "Clinician last name" },
    { kind: "provider", path: "provider.name.given", label: "Clinician first name" },
    // provider.billing_number is not listed: despite its name it has always supplied the licence
    // number, and it keeps doing so for the forms already mapped to it (Public Health "Licence
    // Number" boxes).
    {
        kind: "provider",
        path: "provider.ohip_billing_number",
        label: "Clinician OHIP billing number",
    },
    // As saved for this clinic in the physician profile; 0000 (solo billing) prints as 0000.
    {
        kind: "provider",
        path: "provider.ohip_group_number",
        label: "Clinician OHIP group number",
    },
    {
        kind: "provider",
        path: "provider.licence_number",
        label: "Clinician licence number (CPSO registration)",
    },
    // The clinician's address and phone are the clinic site's: the profile holds neither.
    {
        kind: "provider",
        path: "provider.address.formatted",
        label: "Clinician address (the clinic's, full)",
    },
    {
        kind: "provider",
        path: "provider.address.street",
        label: "Clinician street (the clinic's)",
    },
    { kind: "provider", path: "provider.address.city", label: "Clinician city (the clinic's)" },
    {
        kind: "provider",
        path: "provider.address.province",
        label: "Clinician province (the clinic's)",
    },
    {
        kind: "provider",
        path: "provider.address.postal_code",
        label: "Clinician postal code (the clinic's)",
    },
    { kind: "provider", path: "provider.phone", label: "Clinician phone (the clinic's number)" },
    { kind: "clinic", path: "clinic.name", label: "Clinic name" },
    { kind: "clinic", path: "clinic.organization_name", label: "Clinic organization name" },
    { kind: "clinic", path: "clinic.address", label: "Clinic address (full)" },
    { kind: "clinic", path: "clinic.address.street", label: "Clinic street (number and name)" },
    { kind: "clinic", path: "clinic.address.city", label: "Clinic city" },
    { kind: "clinic", path: "clinic.address.province", label: "Clinic province" },
    { kind: "clinic", path: "clinic.address.postal_code", label: "Clinic postal code" },
    { kind: "clinic", path: "clinic.phone", label: "Clinic phone" },
    { kind: "clinic", path: "clinic.fax", label: "Clinic fax" },
    { kind: "encounter", path: "encounter.date", label: "Visit date (yyyy-mm-dd)" },
    { kind: "encounter", path: "encounter.service_date.year", label: "Visit year" },
    { kind: "encounter", path: "encounter.service_date.month", label: "Visit month" },
    { kind: "encounter", path: "encounter.service_date.day", label: "Visit day" },
    { kind: "encounter", path: "reason", label: "Reason for visit" },
    {
        kind: "encounter",
        path: "encounter.order.additional_clinical_information",
        label: "Clinical information for the order",
    },
];
/** Every name the runtime supplies, including its older aliases for the same values. */
export const CLINIC_DOCUMENT_RECORD_SOURCE_NAMES = {
    patient: [
        "demographics.full_name",
        "full_name",
        "patient.full_name",
        "display_name",
        "patient.name.family",
        "patient.name.given_and_middle",
        "patient.legal_name.family",
        "patient.legal_name.given_and_middle",
        "demographics.date_of_birth",
        "date_of_birth",
        "patient.date_of_birth",
        "dob",
        "patient.date_of_birth.year",
        "patient.date_of_birth.month",
        "patient.date_of_birth.day",
        "demographics.health_card_number",
        "health_card_number",
        "patient.health_card_number",
        "patient.identifiers.ohip_number",
        "coverage.member_id",
        "patient.health_card.version_code",
        "patient.identifiers.ohip_version_code",
        "patient.identifiers.ohipVersionCode",
        "patient.identifiers.ontarioHealthCardVersionCode",
        "demographics.phone",
        "phone",
        "patient.phone",
        "demographics.email",
        "email",
        "patient.email",
        "mrn",
        "patient.mrn",
        "demographics.administrative_sex",
        "administrative_sex",
        "patient.sex",
        "patient.sex.male",
        "patient.sex.female",
        "patient.address",
        "patient.address.formatted",
        "patient.address.street",
        "patient.address.city",
        "patient.address.province",
        "patient.address.postal_code",
    ],
    encounter: [
        "date",
        "encounter.date",
        "encounter.order_date",
        "encounter.service_date.year",
        "encounter.service_date.month",
        "encounter.service_date.day",
        "reason",
        "reason_for_visit",
        "note",
        "note_text",
        "encounter.order.additional_clinical_information",
    ],
    provider: [
        "full_name",
        "provider.full_name",
        "provider.name",
        "display_name",
        "provider.name.family",
        "provider.name.given",
        "signature_name",
        "billing_number",
        "license_number",
        "provider.billing_number",
        "provider.identifiers.cpso_registration_number",
        "provider.licence_number",
        "provider.licenceNumber",
        "provider.registration_number",
        "provider.registrationNumber",
        "provider.identifiers.cpsoRegistrationNumber",
        "provider.ohip_billing_number",
        // The Ontario lab requisition's "Clinician/Practitioner Number" is the OHIP billing number.
        "provider.identifiers.clinician_practitioner_number",
        "provider.identifiers.clinicianPractitionerNumber",
        "provider.ohip_group_number",
        "license_jurisdiction",
        "provider.address.formatted",
        "provider.address.street",
        "provider.address.city",
        "provider.address.province",
        "provider.address.postal_code",
        "provider.phone",
    ],
    clinic: [
        "name",
        "clinic.name",
        "display_name",
        "organization_name",
        "clinic.organization_name",
        "address",
        "clinic.address",
        "clinic.address.formatted",
        "clinic.address.street",
        "clinic.address.city",
        "clinic.address.province",
        "clinic.address.postal_code",
        "phone",
        "clinic.phone",
        "fax",
        "clinic.fax",
    ],
};
/** The renderer finds a record value ignoring case, "." and "-", so matching does the same. */
const comparable = (path) => path.normalize("NFKC").trim().toLocaleLowerCase("en-CA").replace(/[.-]/g, "_");
/** Common names a detector uses for a supplied value. Each maps to exactly one value. */
const SYNONYMS = {
    patient: {
        name: "patient.full_name",
        patient_name: "patient.full_name",
        legal_name: "patient.full_name",
        full_legal_name: "patient.full_name",
        last_name: "patient.name.family",
        family_name: "patient.name.family",
        surname: "patient.name.family",
        first_name: "patient.name.given_and_middle",
        given_name: "patient.name.given_and_middle",
        given_names: "patient.name.given_and_middle",
        legal_last_name: "patient.legal_name.family",
        legal_family_name: "patient.legal_name.family",
        legal_surname: "patient.legal_name.family",
        legal_first_name: "patient.legal_name.given_and_middle",
        legal_given_name: "patient.legal_name.given_and_middle",
        legal_given_names: "patient.legal_name.given_and_middle",
        legal_first_and_middle_names: "patient.legal_name.given_and_middle",
        birth_date: "patient.date_of_birth",
        birthdate: "patient.date_of_birth",
        health_card: "patient.health_card_number",
        health_number: "patient.health_card_number",
        ohip_number: "patient.health_card_number",
        version_code: "patient.health_card.version_code",
        health_card_version: "patient.health_card.version_code",
        health_card_version_code: "patient.health_card.version_code",
        ohip_version_code: "patient.health_card.version_code",
        medical_record_number: "patient.mrn",
        chart_number: "patient.mrn",
        telephone: "patient.phone",
        phone_number: "patient.phone",
        telephone_number: "patient.phone",
        email_address: "patient.email",
        e_mail: "patient.email",
        sex: "patient.sex",
        address: "patient.address",
        home_address: "patient.address",
        street: "patient.address.street",
        street_address: "patient.address.street",
        address_street: "patient.address.street",
        address_line_1: "patient.address.street",
        address_line1: "patient.address.street",
        city: "patient.address.city",
        address_city: "patient.address.city",
        province: "patient.address.province",
        state: "patient.address.province",
        region: "patient.address.province",
        address_province: "patient.address.province",
        address_state: "patient.address.province",
        address_region: "patient.address.province",
        postal_code: "patient.address.postal_code",
        postcode: "patient.address.postal_code",
        zip: "patient.address.postal_code",
        zip_code: "patient.address.postal_code",
        address_postal_code: "patient.address.postal_code",
        address_postcode: "patient.address.postal_code",
    },
    provider: {
        name: "provider.full_name",
        last_name: "provider.name.family",
        family_name: "provider.name.family",
        surname: "provider.name.family",
        first_name: "provider.name.given",
        given_name: "provider.name.given",
        given_names: "provider.name.given",
        licence_number: "provider.licence_number",
        license: "provider.licence_number",
        licence: "provider.licence_number",
        cpso: "provider.licence_number",
        cpso_number: "provider.licence_number",
        cpso_registration_number: "provider.licence_number",
        registration_number: "provider.licence_number",
        ohip_billing_number: "provider.ohip_billing_number",
        ohip_number: "provider.ohip_billing_number",
        ohip_provider_number: "provider.ohip_billing_number",
        ohip_practitioner_number: "provider.ohip_billing_number",
        practitioner_number: "provider.ohip_billing_number",
        clinician_practitioner_number: "provider.ohip_billing_number",
        group_number: "provider.ohip_group_number",
        ohip_group_number: "provider.ohip_group_number",
        billing_group_number: "provider.ohip_group_number",
        telephone: "provider.phone",
        phone_number: "provider.phone",
        address: "provider.address.formatted",
        // Only names that say "address" reach the clinician's site address parts. A bare city,
        // province, state, region or postal code on a clinician is as likely the place of
        // registration or licensure, which the site address must never fill.
        street: "provider.address.street",
        street_address: "provider.address.street",
        address_street: "provider.address.street",
        address_line_1: "provider.address.street",
        address_line1: "provider.address.street",
        address_city: "provider.address.city",
        address_province: "provider.address.province",
        address_state: "provider.address.province",
        address_region: "provider.address.province",
        address_postal_code: "provider.address.postal_code",
        address_postcode: "provider.address.postal_code",
        address_zip: "provider.address.postal_code",
        address_zip_code: "provider.address.postal_code",
    },
    clinic: {
        facility_name: "clinic.name",
        telephone: "clinic.phone",
        phone_number: "clinic.phone",
        fax_number: "clinic.fax",
        street: "clinic.address.street",
        street_address: "clinic.address.street",
        address_street: "clinic.address.street",
        address_line_1: "clinic.address.street",
        address_line1: "clinic.address.street",
        city: "clinic.address.city",
        address_city: "clinic.address.city",
        province: "clinic.address.province",
        state: "clinic.address.province",
        region: "clinic.address.province",
        address_province: "clinic.address.province",
        address_state: "clinic.address.province",
        address_region: "clinic.address.province",
        postal_code: "clinic.address.postal_code",
        postcode: "clinic.address.postal_code",
        zip: "clinic.address.postal_code",
        zip_code: "clinic.address.postal_code",
        address_postal_code: "clinic.address.postal_code",
        address_postcode: "clinic.address.postal_code",
    },
    encounter: {
        visit_date: "encounter.date",
        service_date: "encounter.date",
        reason: "reason",
    },
};
const SUPPLIED = new Map(Object.entries(CLINIC_DOCUMENT_RECORD_SOURCE_NAMES).map(([kind, names]) => [
    kind,
    new Map(names.map((name) => [comparable(name), name])),
]));
/**
 * The name to store for a detected record mapping, or null when the runtime supplies nothing
 * that matches (the field must then be completed by the clinician). A supplied name is kept
 * exactly; a supplied name behind an extra kind prefix, or a known synonym, becomes the name
 * the runtime supplies.
 */
export function supportedClinicDocumentRecordSource(kind, path) {
    const supplied = SUPPLIED.get(kind);
    const key = comparable(path);
    if (supplied?.has(key))
        return path;
    const withoutPrefix = key.startsWith(`${kind}_`)
        ? key.slice(kind.length + 1)
        : kind === "patient" && key.startsWith("demographics_")
            ? key.slice("demographics_".length)
            : key;
    return supplied?.get(withoutPrefix) ?? SYNONYMS[kind][withoutPrefix] ?? null;
}
/**
 * Whether a supplied record value can tick a checkbox or radio option. The renderer ticks an
 * option from a boolean predicate or an exact export-value match. Sex predicates keep the
 * clinical meaning separate from a PDF's arbitrary on-state, including "0", "1" or "Female".
 */
export function clinicDocumentRecordSourceCanTick(kind, path, exportState) {
    const supplied = supportedClinicDocumentRecordSource(kind, path);
    return (kind === "patient" &&
        supplied !== null &&
        ((["patient_sex_male", "patient_sex_female"].includes(comparable(supplied)) &&
            typeof exportState === "string" &&
            exportState.length > 0) ||
            (comparable(supplied) === "patient_sex" && (exportState === "M" || exportState === "F"))));
}
//# sourceMappingURL=clinic-document-record-sources.js.map